import React from "react";
import { useParams } from "react-router-dom";
import { useTenant } from "../../context/TenantContext";
import { useClient } from "../../context/ClientContext";
import CookiePolicyModal from "./modal-cookie";
import CookieSettingsModal from "./modal-cookie-settings";
import { useLanguage } from "../../context/LanguageContext";
import { saveCookiePreferences } from "../../utils/cookieManager";
import apiService from "../../services/api.service";
import { getCloudfrontAssetUrl } from "../../utils/imageUtils";
// import logger from "../../utils/logger";

const Footer = ({ className = "" }) => {
  const currentYear = new Date().getFullYear();
  const { tenanturlname } = useParams();
  const { tenant } = useTenant();
  const { client, updateClient, isAuthenticated } = useClient();
  const [isCookiePolicyOpen, setIsCookiePolicyOpen] = React.useState(false);
  const [isCookieSettingsOpen, setIsCookieSettingsOpen] = React.useState(false);
  const { t } = useLanguage();
  const tenantLogoUrl = getCloudfrontAssetUrl(tenant?.tenantlogo);

  const savePolicies = async (policies) => {
    saveCookiePreferences(tenant?.tenantid, policies);
    window.__cookieConsent = policies;
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: policies }));

    if (isAuthenticated && client?.clientId) {
      try {
        await apiService.post('/api/clients/cookiepoliciesaction', {
          tenantid: tenant?.tenantid || null,
          clientid: client?.clientId || null,
          policies,
        });
        if (updateClient) {
          updateClient({ cookiePreference: JSON.stringify({ ...policies, timestamp: new Date().toISOString() }) });
        }
      } catch (err) {
        console.error('Footer - cookie policy API error', err);
      }
    }
  };

  const handleCookieAccept = () => savePolicies({ essential: true, analytics: true });
  const handleCookieDeny = () => savePolicies({ essential: true, analytics: false });

  // Get footer links from tenant config or use fallback
  const configLinks = tenant?.tenantcontentconfig?.footer?.links || [];

  const footerLinks =
    configLinks.length > 0
      ? configLinks.map((link) => {
          // Check if it's an external link using the external property
          const isExternalLink = link.external === true;
          
          return {
            label: link.text,
            href: isExternalLink ? link.link : `/${tenanturlname}${link.link}`,
            target: link.target,
            onClick:
              link.text === "Cookie Policy"
                ? () => setIsCookiePolicyOpen(true)
                : undefined,
          };
        })
      : [
          //   {
          //     label: t("footer.termsConditions") || "Terms and Conditions",
          //     href: `/${tenanturlname}/terms`,
          //     target: "_self",
          //   },
          //   {
          //     label: t("footer.globalPrivacyPolicy") || "Global Privacy Policy", // ← ADD
          //     href: "#global-privacy",
          //     target: "_self",
          //   },
          //   {
          //     label: t("footer.servicePrivacyPolicy") || "Service Privacy Policy", // ← ADD
          //     href: "#service-privacy",
          //     target: "_self",
          //   },
          //   {
          //     label: t("footer.cookiePolicy") || "Cookie Policy", // ← ADD
          //     href: "#cookie-policy",
          //     target: "_self",
          //     onClick: () => setIsCookiePolicyOpen(true),
          //   },
          //   {
          //     label: t("footer.faq") || "FAQ", // ← ADD
          //     href: `/${tenanturlname}/faq`,
          //     target: "_self",
          //   },

          { label: "Terms and Conditions", href: "#terms", target: "_self" },
          {
            label: "Global Privacy Policy",
            href: "#global-privacy",
            target: "_self",
          },
          {
            label: "Service Privacy Policy",
            href: "#service-privacy",
            target: "_self",
          },
          {
            label: "Cookie Policy",
            href: "#cookie-policy",
            target: "_self",
            onClick: () => setIsCookiePolicyOpen(true),
          },
          {
            label: "Cookie Settings",
            href: "#cookie-settings",
            target: "_self",
            onClick: () => setIsCookieSettingsOpen(true),
          },
          { label: "FAQ", href: "#faq", target: "_self" },
        ];

  const handleLinkClick = (e, link) => {
    if (link.onClick) {
      e.preventDefault();
      link.onClick();
    }
  };

  return (
    <footer
      className={`bg-theme-primary text-theme-footer-text py-8 px-4  ${className}`}
    >
      <div className="max-w-5xl mx-auto">
        {/* Desktop Layout */}
        <div className="hidden md:block">
          <div className="space-y-6 desktop-footer">
            {/* Links Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 desktop-footer-links">
              {footerLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  target={link.target || "_self"}
                  rel={
                    link.target === "_blank" ? "noopener noreferrer" : undefined
                  }
                  onClick={(e) => handleLinkClick(e, link)}
                  className="text-theme-neutral hover:text-gray-200 transition-colors duration-200 text-xs font-normal py-1 cursor-pointer"
                >
                  {link.label}
                </a>
              ))}
            </div>

            {/* Divider */}
            <div className="border-t border-white/20"></div>

            {/* Bottom Section */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 desktop-footer-logo">
              <div className="flex items-center">
                {tenantLogoUrl ? (
                  <img
                    src={tenantLogoUrl}
                    alt={tenant?.tenantname || "Logo"}
                    className="h-8 w-auto filter brightness-0 invert"
                  />
                ) : (
                  <span className="text-xl font-bold text-white">
                    {tenant?.tenantname || "Logo"}
                  </span>
                )}
              </div>
              <div className="text-center sm:text-right">
                <div className="flex flex-row items-center text-[10px] font-normal text-theme-neutral">
                  <span className="text-[16px] mr-1">©</span>Copyright {currentYear}, Visa Inc. All Rights Reserved.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden">
          <div className="space-y-6">
            {/* Links List */}
            <div className="space-y-3">
              {footerLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  target={link.target || "_self"}
                  rel={
                    link.target === "_blank" ? "noopener noreferrer" : undefined
                  }
                  onClick={(e) => handleLinkClick(e, link)}
                  className="block text-theme-neutral text-left hover:text-gray-200 transition-colors duration-200 text-xs font-normal py-1 cursor-pointer"
                >
                  {link.label}
                </a>
              ))}
            </div>

            {/* Logo */}
            <div className="">
              {tenantLogoUrl ? (
                <img
                  src={tenantLogoUrl}
                  alt={tenant?.tenantname || "Logo"}
                  className="h-8 w-auto filter brightness-0 invert"
                />
              ) : (
                <span className="text-xl font-bold text-white">
                  {tenant?.tenantname || "Logo"}
                </span>
              )}
              {/* Copyright */}
              <div className="text-left mt-2 ">
                <p className="text-[10px] font-normal text-theme-neutral">
                  <span className="text-[13px] mr-1">©</span>Copyright {currentYear}, Visa Inc. All Rights Reserved.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Policy Modal */}
      <CookiePolicyModal
        open={isCookiePolicyOpen}
        onOpenChange={setIsCookiePolicyOpen}
        onAccept={handleCookieAccept}
        onDeny={handleCookieDeny}
      />

      {/* Cookie Settings Modal */}
      <CookieSettingsModal
        open={isCookieSettingsOpen}
        onOpenChange={setIsCookieSettingsOpen}
      />
    </footer>
  );
};

export default Footer;
